import { useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface PageSEOProps {
  pageSlug: string;
  fallbackTitle?: string;
  fallbackDescription?: string;
}

const PageSEO = ({ pageSlug, fallbackTitle, fallbackDescription }: PageSEOProps) => {
  const getImageUrl = (path: string) => {
    if (!path) return null;
    const { data } = supabase.storage.from('site-assets').getPublicUrl(path);
    return data.publicUrl;
  };

  useEffect(() => {
    const updatePageSEO = async () => {
      const { data: pageData } = await supabase
        .from('page_seo')
        .select('*')
        .eq('page_slug', pageSlug)
        .maybeSingle();

      const { data: siteData } = await supabase
        .from('site_settings')
        .select('*')
        .limit(1)
        .maybeSingle();

      // Use page-specific data or fallback to site defaults
      const title = pageData?.meta_title || fallbackTitle || siteData?.site_title || '';
      const description = pageData?.meta_description || fallbackDescription || siteData?.site_description || '';
      const keywords = pageData?.meta_keywords || siteData?.meta_keywords || '';
      const canonicalUrl = pageData?.canonical_url || siteData?.canonical_url || '';

      // Update page title
      document.title = title;

      // Update meta tags
      updateMetaTag('name', 'description', description);
      if (keywords) {
        updateMetaTag('name', 'keywords', keywords);
      }
      
      if (canonicalUrl) {
        updateCanonicalUrl(canonicalUrl);
      }

      // Update Open Graph tags
      const ogTitle = pageData?.og_title || title;
      const ogDescription = pageData?.og_description || description;
      
      updateMetaTag('property', 'og:title', ogTitle);
      updateMetaTag('property', 'og:description', ogDescription);
      updateMetaTag('property', 'og:type', siteData?.og_type || 'website');
      updateMetaTag('property', 'og:locale', siteData?.og_locale || 'nl_NL');
      
      // Use page-specific OG image or fallback to site default
      const ogImagePath = pageData?.og_image_path || siteData?.og_image_path;
      if (ogImagePath) {
        const ogImageUrl = getImageUrl(ogImagePath);
        if (ogImageUrl) {
          updateMetaTag('property', 'og:image', ogImageUrl);
        }
      }

      // Update Twitter tags using Open Graph data
      updateMetaTag('name', 'twitter:card', siteData?.twitter_card || 'summary_large_image');
      updateMetaTag('name', 'twitter:title', ogTitle);
      updateMetaTag('name', 'twitter:description', ogDescription);
      
      if (ogImagePath) {
        const twitterImageUrl = getImageUrl(ogImagePath);
        if (twitterImageUrl) {
          updateMetaTag('name', 'twitter:image', twitterImageUrl);
        }
      }

      if (siteData?.twitter_site) {
        updateMetaTag('name', 'twitter:site', siteData.twitter_site);
      }
      if (siteData?.twitter_creator) {
        updateMetaTag('name', 'twitter:creator', siteData.twitter_creator);
      }
    };

    updatePageSEO();
  }, [pageSlug, fallbackTitle, fallbackDescription]);

  return null;
};

const updateMetaTag = (attribute: string, name: string, content: string) => {
  let meta = document.querySelector(`meta[${attribute}="${name}"]`);
  if (meta) {
    meta.setAttribute('content', content);
  } else {
    meta = document.createElement('meta');
    meta.setAttribute(attribute, name);
    meta.setAttribute('content', content);
    document.head.appendChild(meta);
  }
};

const updateCanonicalUrl = (url: string) => {
  let link = document.querySelector('link[rel="canonical"]');
  if (link) {
    link.setAttribute('href', url);
  } else {
    link = document.createElement('link');
    link.setAttribute('rel', 'canonical');
    link.setAttribute('href', url);
    document.head.appendChild(link);
  }
};

export default PageSEO;